import { useShallow } from 'zustand/shallow';
import { Drawer } from '@nula/core-ui';
import { CommentSidebar } from '@/features/comments/components/comment-sidebar';
import { HistoryPanel } from '@/features/editor/components/history-panel';
import { AttendeesSidePanel } from '@/features/recordings/components/attendees-side-panel';
import { CalendarEventPanel } from '@/features/calendar/components/calendar-event-panel';
import { AssistantOverlay } from '@/features/assistant/components/assistant-overlay';
import { AssistantSidebar } from '@/features/assistant/components/assistant-sidebar';
import { AssistantHistoryPanel } from '@/features/assistant/components/assistant-history-panel';
import { ExpandableBar } from '@/layout/expandable-bar';
import { useActiveTabIsConversation } from '@/hooks/navigation';
import { shouldShowAssistantBar } from './should-show-assistant-bar';
import { useUIStore } from '@/stores/use-ui-store';
import { useCalendarStore } from '@/stores/use-calendar-store';
import { useConversationRegistryStore } from '@/features/assistant/stores/use-conversation-registry-store';
import { sessionOpenTab } from '@/session';
import { motion } from 'motion/react';
import styles from './app-overlays.module.css';

interface AppOverlaysProps {
  panelWidth: number | string;
  activeEntityId: string | null;
}

export function AppOverlays({ panelWidth, activeEntityId }: AppOverlaysProps) {
  const {
    isCommentsPanelOpen,
    isVersionHistoryOpen, setVersionHistoryOpen,
    isAttendeesPanelOpen, attendeesPanelEntityId, attendeesPanelSurfaceId, setAttendeesPanelOpen,
    assistantMode, setAssistantMode,
    isAssistantSidebarOpen, toggleAssistantSidebar,
    isAssistantHistoryOpen, setAssistantHistoryOpen,
  } = useUIStore(useShallow((s) => ({
    isCommentsPanelOpen: s.isCommentsPanelOpen,
    isVersionHistoryOpen: s.isVersionHistoryOpen, setVersionHistoryOpen: s.setVersionHistoryOpen,
    isAttendeesPanelOpen: s.isAttendeesPanelOpen,
    attendeesPanelEntityId: s.attendeesPanelEntityId,
    attendeesPanelSurfaceId: s.attendeesPanelSurfaceId,
    setAttendeesPanelOpen: s.setAttendeesPanelOpen,
    assistantMode: s.assistantMode, setAssistantMode: s.setAssistantMode,
    isAssistantSidebarOpen: s.isAssistantSidebarOpen, toggleAssistantSidebar: s.toggleAssistantSidebar,
    isAssistantHistoryOpen: s.isAssistantHistoryOpen, setAssistantHistoryOpen: s.setAssistantHistoryOpen,
  })));

  const activeTabIsConversation = useActiveTabIsConversation();

  // The ambient assistant (overlay + sidebar) renders the conversation keyed by
  // ambientConversationId once the user's first send promotes it. Expand opens a
  // tab on that SAME UUID — the registry hands over the identical live bundle
  // (zero-copy hand-off), so the tab continues the live run. Null before the
  // first send → expand mints a fresh conversation.
  const ambientConversationId = useConversationRegistryStore((s) => s.ambientConversationId);

  return (
    <>
      <Drawer open={isCommentsPanelOpen && !!activeEntityId} onClose={() => useUIStore.setState({ isCommentsPanelOpen: false })} width={panelWidth} bodyStyle={{ padding: 0 }} floating>
        {activeEntityId && <CommentSidebar entityId={activeEntityId} />}
      </Drawer>
      <Drawer open={isVersionHistoryOpen} onClose={() => setVersionHistoryOpen(false)} width={panelWidth} bodyStyle={{ padding: 0 }} floating>
        <HistoryPanel />
      </Drawer>
      <AttendeesDrawer
        width={panelWidth}
        open={isAttendeesPanelOpen}
        onClose={() => setAttendeesPanelOpen(false)}
        targetEntityId={attendeesPanelEntityId ?? activeEntityId}
        targetSurfaceId={attendeesPanelEntityId ? attendeesPanelSurfaceId : null}
      />
      <CalendarEventDrawer width={panelWidth} />

      <ExpandableBar
        visible={shouldShowAssistantBar(assistantMode, activeTabIsConversation)}
        icon={
          <motion.div
            className={styles.assistantOrb}
            animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        }
        placeholder="Ask Nula"
        onExpand={() => setAssistantMode('expanded')}
      />

      <AssistantOverlay
        isOpen={assistantMode === 'expanded'}
        activeEntityId={activeEntityId ?? null}
        onClose={() => setAssistantMode('collapsed')}
        onOpenFullTab={() => {
          // Open the tab on the ambient conversation's UUID so the tab acquires
          // the SAME live bundle (zero-copy hand-off). If nothing's been sent yet
          // (ambient null), mint a fresh conversation for the tab. Then clear the
          // ambient pointer so reopening the overlay starts a fresh chat — the
          // conversation has "moved" into the tab (the confirmed single-owner UX).
          sessionOpenTab(ambientConversationId ?? crypto.randomUUID(), 'conversation');
          if (ambientConversationId) {
            useConversationRegistryStore.getState().setAmbientConversationId(null);
          }
          setAssistantMode('fullTab');
        }}
      />

      <Drawer open={isAssistantSidebarOpen} onClose={toggleAssistantSidebar} width={panelWidth} bodyStyle={{ padding: 0 }} floating mask={false}>
        <AssistantSidebar onClose={toggleAssistantSidebar} />
      </Drawer>

      {/* Conversation history — overlays the assistant chat drawer at the right
          edge (open-over). Picking a conversation loads it + closes this panel.
          data-assistant-surface marks this as part of the assistant: the
          floating overlay's click-outside handler must not treat clicks in
          here as a dismissal, since the drawer portals outside its ref. */}
      <Drawer open={isAssistantHistoryOpen} onClose={() => setAssistantHistoryOpen(false)} width={panelWidth} bodyStyle={{ padding: 0 }} floating mask={false}>
        <AssistantHistoryPanel />
      </Drawer>

    </>
  );
}

function CalendarEventDrawer({ width }: { width: number | string }) {
  const { isPanelOpen, selectedEvent, closePanel } = useCalendarStore();
  if (!isPanelOpen || !selectedEvent) return null;
  return (
    <Drawer open onClose={closePanel} width={width} bodyStyle={{ padding: 0 }} floating>
      <CalendarEventPanel event={selectedEvent} onClose={closePanel} />
    </Drawer>
  );
}

function AttendeesDrawer({
  width, open, onClose, targetEntityId, targetSurfaceId,
}: {
  width: number | string;
  open: boolean;
  onClose: () => void;
  /** The panel opener's own entity, preferred over the active entity so split
   * view opens the panel for the chip that was clicked, not whichever pane is
   * active. Falls back to the active entity when the opener didn't specify one. */
  targetEntityId: string | null;
  /** The opener's active surface, if any. Only meaningful alongside an
   * explicit targetEntityId — never applied to the active-entity fallback,
   * since that entity's active surface may differ from the opener's. */
  targetSurfaceId: string | null;
}) {
  const canShow = open && !!targetEntityId;
  return (
    <Drawer open={canShow} onClose={onClose} width={width} bodyStyle={{ padding: 0 }} mask={false} floating>
      {targetEntityId && (
        <AttendeesSidePanel entityId={targetEntityId} surfaceId={targetSurfaceId} onClose={onClose} />
      )}
    </Drawer>
  );
}
